<template>
  <div
    class="modal fade"
    id="recognized-program-view-modal"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-0">Program Details</h5>
            <small class="text-muted" v-if="program">
              {{ program.hei_name || program.hei_code }}
            </small>
          </div>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body" v-if="program">
          <div class="detail-grid mb-4">
            <div class="detail-item">
              <span class="detail-label">Program</span>
              <span class="detail-value">{{ program.program_name }}</span>
              <small v-if="program.former_name" class="text-muted d-block">
                Formerly: {{ program.former_name }}
              </small>
            </div>
            <div class="detail-item">
              <span class="detail-label">Major</span>
              <span class="detail-value">{{ program.major || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Discipline Code</span>
              <span class="detail-value">{{ program.discipline_code || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Academic Year</span>
              <span class="detail-value">{{ program.academic_year || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Permit No.</span>
              <span class="detail-value">
                <button
                  v-if="program.permit_no_file_path"
                  type="button"
                  class="btn btn-link px-0 py-0"
                  @click="openUrl(program.permit_no_file_path)"
                >
                  <i class="bi bi-file-pdf"></i> {{ program.permit_no }}
                </button>
                <span v-else>{{ program.permit_no || "—" }}</span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Date Issued</span>
              <span class="detail-value">
                <span v-if="program.date_issued">{{
                  program.date_issued | standardDate
                }}</span>
                <span v-else>—</span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="detail-value">
                <span
                  class="badge badge-pill"
                  :class="
                    program.status === 'Active' ? 'badge-success' : 'badge-danger'
                  "
                >
                  {{ program.status || "—" }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Accreditation</span>
              <span class="detail-value">{{
                program.accreditation_level || "—"
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Program Head</span>
              <span class="detail-value">{{ program.program_chair || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Level</span>
              <span class="detail-value">{{ program.level_code || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Board Type</span>
              <span class="detail-value">{{ boardTypeLabel }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Validity</span>
              <span class="detail-value">{{ program.validity || "—" }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Supervisor</span>
              <span class="detail-value">{{ program.supervisor_name || "—" }}</span>
            </div>
            <div class="detail-item" v-if="program.atop_cat || program.atop_serial">
              <span class="detail-label">ATOP</span>
              <span class="detail-value">
                {{ program.atop_cat || "—" }}
                <span v-if="program.atop_serial">
                  · {{ program.atop_serial }}
                  <span v-if="program.atop_year"> ({{ program.atop_year }})</span>
                </span>
              </span>
            </div>
          </div>

          <div class="trail-section">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="font-weight-bold mb-0">Permit Issuance Trail</h6>
              <small class="text-muted">From Program Permits module</small>
            </div>
            <div v-if="loadingPermits" class="text-center py-3 text-muted">
              <span class="spinner-border spinner-border-sm mr-2"></span>
              Loading permits...
            </div>
            <div v-else-if="!permits.length" class="text-center py-3 text-muted">
              No generated permits found for this program.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Permit No.</th>
                    <th>Type</th>
                    <th>Subtype</th>
                    <th>Effectivity</th>
                    <th>Date Issued</th>
                    <th>Generated By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="permit in permits" :key="permit.id">
                    <td>
                      {{ permit.type_abbrev }} No.
                      {{ String(permit.number).padStart(3, "0") }} s.
                      {{ permit.series }}
                    </td>
                    <td>{{ permit.type }}</td>
                    <td>
                      {{
                        permit.additional_fields &&
                        permit.additional_fields.subtype
                          ? permit.additional_fields.subtype
                          : "—"
                      }}
                    </td>
                    <td>{{ permit.academic_year || "—" }}</td>
                    <td>{{ permit.date_issued | shortDate }}</td>
                    <td>{{ permit.generated_by || "—" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="program && program.program_id"
            type="button"
            class="btn btn-outline-primary mr-auto"
            @click="openCatalogProfile"
          >
            View catalog profile
          </button>
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RecognizedProgramViewModal",
  data() {
    return {
      program: null,
      permits: [],
      loadingPermits: false,
      prefix: "https://drive.google.com/file/d/",
    };
  },
  computed: {
    boardTypeLabel() {
      if (!this.program || !this.program.board_type) return "—";
      return this.program.board_type === "board" ? "Board" : "Non-Board";
    },
  },
  methods: {
    open(program, options = {}) {
      this.program = {
        ...program,
        hei_code: program.hei_code || options.hei_code || "",
        program_id: program.program_id,
        program_name:
          program.program_name ||
          program.name ||
          "Unnamed program",
      };
      this.permits = [];
      this.$nextTick(() => {
        $("#recognized-program-view-modal").modal("show");
        this.loadPermits();
      });
    },
    openCatalogProfile() {
      if (!this.program || !this.program.program_id) return;
      $("#recognized-program-view-modal").modal("hide");
      this.$router.push({
        name: "ProgramDetails",
        params: {
          id: this.program.program_id,
        },
      });
    },
    openUrl(path) {
      window.open(this.prefix + path + "/view", "_blank");
    },
    loadPermits() {
      const heiCode = this.program && this.program.hei_code;
      const programId = this.program && this.program.program_id;

      if (!heiCode || !programId) {
        this.permits = [];
        return;
      }

      this.loadingPermits = true;
      axios
        .get("api/permits/by-hei-program", {
          params: {
            hei_code: heiCode,
            program_id: programId,
          },
        })
        .then((res) => {
          this.permits = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.permits = [];
          toastr.error("Failed to load permit trail.", "Error");
        })
        .finally(() => {
          this.loadingPermits = false;
        });
    },
  },
};
</script>

<style scoped>
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.detail-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: #768192;
  margin-bottom: 0.15rem;
}

.detail-value {
  color: #2b3a55;
  font-weight: 500;
}

.trail-section {
  border-top: 1px solid rgba(0, 0, 21, 0.08);
  padding-top: 1rem;
}

@media (max-width: 767px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
